/**
 * Личный порядок колонок и групп (§4.2).
 *
 * Набор колонок («что показывать») живёт в ссылке — им делятся. Порядок («в
 * каком порядке») — личная привычка, как плотность строк, поэтому он лежит в
 * localStorage и в ссылку не попадает: иначе любой перетаскиванием менял бы
 * вид у всех, кому отправил ссылку.
 *
 * Хранится не весь список колонок, а только то, что человек переставил: новые
 * колонки и новые группы сами встают на свои места из порядка по умолчанию.
 */

export interface ColumnOrder {
  /** Порядок групп. Группы, которых здесь нет, идут после в порядке по умолчанию. */
  groups: string[]
  /** Группа → порядок колонок внутри неё. */
  columns: Record<string, string[]>
}

const STORAGE_KEY = 'bm.columnOrder'
const EMPTY: ColumnOrder = { groups: [], columns: {} }

let current: ColumnOrder = EMPTY
let loaded = false
const listeners = new Set<() => void>()

function read(): ColumnOrder {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return EMPTY
    const parsed = JSON.parse(raw) as Partial<ColumnOrder> | null
    if (!parsed || typeof parsed !== 'object') return EMPTY
    const groups = Array.isArray(parsed.groups)
      ? parsed.groups.filter((item): item is string => typeof item === 'string')
      : []
    const columns: Record<string, string[]> = {}
    if (parsed.columns && typeof parsed.columns === 'object') {
      for (const [group, list] of Object.entries(parsed.columns)) {
        if (!Array.isArray(list)) continue
        columns[group] = list.filter((item): item is string => typeof item === 'string')
      }
    }
    return { groups, columns }
  } catch {
    // Приватный режим или битое значение — работаем с порядком по умолчанию.
    return EMPTY
  }
}

function write(order: ColumnOrder): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(order))
  } catch {
    /* не критично: порядок доживёт до конца сессии */
  }
}

export function getColumnOrder(): ColumnOrder {
  if (!loaded) {
    current = read()
    loaded = true
  }
  return current
}

function commit(next: ColumnOrder): void {
  current = next
  loaded = true
  write(next)
  for (const listener of listeners) listener()
}

export function setGroupOrder(groups: string[]): void {
  commit({ ...getColumnOrder(), groups })
}

export function setColumnOrderIn(group: string, codes: string[]): void {
  const order = getColumnOrder()
  commit({ ...order, columns: { ...order.columns, [group]: codes } })
}

export function resetColumnOrder(): void {
  commit(EMPTY)
}

export function subscribeColumnOrder(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Список после переноса элемента на новое место. Если элемента в списке нет
 * (его переставили первым, а порядок ещё не сохраняли), он просто вставляется.
 */
export function moved<T>(list: readonly T[], item: T, before: T | null): T[] {
  const rest = list.filter((entry) => entry !== item)
  if (before === null) return [...rest, item]
  const index = rest.indexOf(before)
  if (index === -1) return [...rest, item]
  return [...rest.slice(0, index), item, ...rest.slice(index)]
}

/**
 * Полный порядок: сначала то, что человек переставил, затем всё остальное в
 * порядке по умолчанию. Так новая колонка не прыгает в конец списка только
 * потому, что её не было в момент последнего перетаскивания.
 */
export function applyOrder<T extends string>(saved: readonly string[], all: readonly T[]): T[] {
  const known = new Set(all)
  const head = saved.filter((code): code is T => known.has(code as T))
  const seen = new Set(head)
  return [...head, ...all.filter((code) => !seen.has(code))]
}
